import { useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  MessageSquare,
  Video,
  FolderOpen,
  Settings,
  ChevronLeft,
  ChevronRight,
  Users,
  Calendar,
  ExternalLink,

  Sun,
  X,
  GitBranch,
  Mail,
} from 'lucide-react';
import { clsx } from 'clsx';
import type { View } from '../../App';
import { prefetchView } from '../../App';
import { useChatStore, useTeamStore, useNotificationStore } from '../../stores';
import { adminUrl } from '../../lib/urls';
import { useLicenseFeature } from '../../hooks/useLicenseFeatures';

interface SidebarProps {
  currentView: View;
  onNavigate: (view: View) => void;
  collapsed: boolean;
  onToggle: () => void;
  /** Mobile drawer open state */
  mobileOpen?: boolean;
  /** Callback to close mobile drawer */
  onMobileClose?: () => void;
}

const navItems: { id: View; label: string; icon: typeof MessageSquare }[] = [
  { id: 'today',     label: 'Today',     icon: Sun },
  { id: 'chat',      label: 'Chat',      icon: MessageSquare },
  { id: 'mail',      label: 'Mail',      icon: Mail },
  { id: 'teams',     label: 'Teams',     icon: Users },
  { id: 'meet',      label: 'Meet',      icon: Video },
  { id: 'files',     label: 'Files',     icon: FolderOpen },
  { id: 'calendar',  label: 'Calendar',  icon: Calendar },
  { id: 'workflows', label: 'Workflows', icon: GitBranch },
];

const ADMIN_URL = adminUrl();

/** Compute total unread badges for sidebar nav items */
function useSidebarBadges(): Record<string, number> {
  const rooms = useChatStore((s) => s.rooms);
  const teams = useTeamStore((s) => s.teams);
  const notifUnread = useNotificationStore((s) => s.unreadCount);

  const chatUnread = rooms.reduce((sum, r) => sum + (r.unreadCount ?? 0), 0);
  const teamUnread = teams.reduce((sum, t) => {
    const channelTotal = (t.channels ?? []).reduce((cs, ch) => cs + (ch.unreadCount ?? 0), 0);
    return sum + channelTotal;
  }, 0);

  return {
    today: notifUnread,
    chat: chatUnread,
    teams: teamUnread,
  };
}

export function Sidebar({ currentView, onNavigate, collapsed, onToggle, mobileOpen = false, onMobileClose }: SidebarProps) {
  const { t } = useTranslation();
  const navRef = useRef<HTMLElement>(null);
  const badges = useSidebarBadges();

  // Mail is an add-on: not shown when the license never included it (locked mail stays
  // readable, so the entry stays)
  const mailHidden = useLicenseFeature('mail') === 'hidden';
  const visibleNavItems = navItems.filter((i) => !(i.id === 'mail' && mailHidden));

  // Close mobile drawer on Escape
  useEffect(() => {
    if (!mobileOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onMobileClose?.();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [mobileOpen, onMobileClose]);

  /** Arrow-key navigation within the sidebar nav items */
  const handleNavKeyDown = useCallback((e: React.KeyboardEvent<HTMLElement>) => {
    if (!navRef.current) return;
    const buttons = Array.from(navRef.current.querySelectorAll<HTMLElement>('button[data-nav-item], a[data-nav-item]'));
    const idx = buttons.indexOf(e.target as HTMLElement);
    if (idx === -1) return;

    let next = -1;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      next = idx < buttons.length - 1 ? idx + 1 : 0;
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      next = idx > 0 ? idx - 1 : buttons.length - 1;
    } else if (e.key === 'Home') {
      e.preventDefault();
      next = 0;
    } else if (e.key === 'End') {
      e.preventDefault();
      next = buttons.length - 1;
    }
    if (next >= 0) buttons[next]?.focus();
  }, []);

  const sidebarContent = (isMobileDrawer: boolean) => (
    <aside
      className={clsx(
        'flex flex-col border-r border-nord-3/40 bg-nord-1 transition-all duration-200',
        isMobileDrawer
          ? 'w-64' // fixed width in mobile drawer
          : collapsed ? 'w-sidebar-collapsed' : 'w-sidebar',
        // On mobile, the sidebar is hidden (rendered inside overlay instead)
        !isMobileDrawer && 'hidden md:flex',
      )}
    >
      {/* Logo */}
      <div className="flex h-14 items-center gap-2.5 border-b border-nord-3/40 px-4">
        <img src="/logo-mark.svg" alt="Eurion" className="h-7 w-7 shrink-0" />
        {(isMobileDrawer || !collapsed) && (
          <span className="text-lg font-semibold text-nord-6 tracking-tight flex-1">Eurion</span>
        )}
        {isMobileDrawer && (
          <button
            onClick={onMobileClose}
            aria-label={t('layoutUi.closeMenu', 'Close menu')}
            className="rounded-lg p-1.5 text-nord-4 hover:bg-nord-2 hover:text-nord-6 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav ref={navRef} aria-label={t('nav.mobile', 'Main navigation')} className="flex-1 space-y-0.5 p-2" onKeyDown={handleNavKeyDown}>
        {visibleNavItems.map((item) => {
          const Icon = item.icon;
          const active = currentView === item.id;
          const badge = badges[item.id] ?? 0;
          const showLabel = isMobileDrawer || !collapsed;
          return (
            <button
              key={item.id}
              data-nav-item
              onClick={() => onNavigate(item.id)}
              onMouseEnter={() => prefetchView(item.id)}
              aria-current={active ? 'page' : undefined}
              tabIndex={active ? 0 : -1}
              className={clsx(
                'sidebar-item w-full',
                active && 'sidebar-item-active',
              )}
              title={!showLabel ? t('nav.' + item.id) : undefined}
            >
              <div className="relative">
                <Icon className={clsx('h-5 w-5 shrink-0', active && 'text-nord-8')} />
                {badge > 0 && !showLabel && (
                  <span className="absolute -top-1.5 -right-1.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-nord-11 px-1 text-[10px] font-bold text-white" aria-label={t('nav.unread', { n: badge > 99 ? '99+' : badge })}>
                    {badge > 99 ? '99+' : badge}
                  </span>
                )}
              </div>
              {showLabel && (
                <>
                  {/* text-left: a <button> centres its text, and flex-1 stretches the label across the row */}
                  <span className="text-sm flex-1 text-left">{t('nav.' + item.id)}</span>
                  {badge > 0 && (
                    <span className="flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-nord-11 px-1.5 text-[11px] font-bold text-white" aria-label={t('nav.unread', { n: badge > 99 ? '99+' : badge })}>
                      {badge > 99 ? '99+' : badge}
                    </span>
                  )}
                </>
              )}
            </button>
          );
        })}

        {/* Settings + Admin Portal */}
        <div className="mt-3 pt-3 border-t border-nord-3/30 space-y-0.5">
          <button
            data-nav-item
            onClick={() => onNavigate('settings')}
            onMouseEnter={() => prefetchView('settings')}
            aria-current={currentView === 'settings' ? 'page' : undefined}
            tabIndex={currentView === 'settings' ? 0 : -1}
            className={clsx(
              'sidebar-item w-full',
              currentView === 'settings' && 'sidebar-item-active',
            )}
            title={!isMobileDrawer && collapsed ? t('nav.settings') : undefined}
          >
            <Settings className={clsx('h-5 w-5 shrink-0', currentView === 'settings' && 'text-nord-8')} />
            {(isMobileDrawer || !collapsed) && <span className="text-sm">{t('nav.settings')}</span>}
          </button>
          <a
            data-nav-item
            href={ADMIN_URL}
            target="_blank"
            rel="noopener noreferrer"
            tabIndex={-1}
            className="sidebar-item w-full flex items-center gap-3 text-nord-4 hover:text-nord-6"
            title={!isMobileDrawer && collapsed ? t('nav.admin') : undefined}
          >
            <ExternalLink className="h-5 w-5 shrink-0" />
            {(isMobileDrawer || !collapsed) && (
              <>
                <span className="flex-1 text-sm">{t('nav.admin')}</span>
                <ExternalLink className="h-3 w-3 text-nord-muted/50" />
              </>
            )}
          </a>
        </div>
      </nav>

      {/* Collapse toggle — desktop only */}
      {!isMobileDrawer && (
        <div className="border-t border-nord-3/30 p-2">
          <button
            onClick={onToggle}
            aria-label={collapsed ? t('layoutUi.expandSidebar', 'Expand sidebar') : t('layoutUi.collapseSidebar', 'Collapse sidebar')}
            className="sidebar-item w-full justify-center"
          >
            {collapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <ChevronLeft className="h-4 w-4" />
            )}
          </button>
        </div>
      )}
    </aside>
  );

  return (
    <>
      {/* Desktop sidebar (always rendered, hidden on mobile via className) */}
      {sidebarContent(false)}

      {/* Mobile drawer overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 flex md:hidden">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 transition-opacity"
            onClick={onMobileClose}
            aria-hidden="true"
          />
          {/* Drawer panel */}
          <div className="relative z-10 animate-slide-in-left">
            {sidebarContent(true)}
          </div>
        </div>
      )}
    </>
  );
}
